import { useContext, useEffect } from 'react'
import { classes } from 'typestyle'

/* Context ====================================================================================== */
import { NavigationContext } from '../../__aeki__/contexts/navigation'

/* Core ========================================================================================= */
import { addSnippet } from '../../__aeki__/core/snippet-manager'
import { formatDate } from '../../__aeki__/share/format'
import { withRouter } from '../../__aeki__/contexts/with-router'

/* Hooks ======================================================================================== */
import useFetch from '../../__aeki__/hooks/use-fetch'

/* Components =================================================================================== */
import { Box } from '../../__aeki__/components/box/box.component'
import { Button } from '../../__aeki__/components/button'
import { Line } from '../../__aeki__/components/line'
import { Modal } from '../../__aeki__/components/modal'
import { Snippet, SnippetHead } from '../../__aeki__/modules/layouts/snippet'
import { Text } from '../../__aeki__/components/text/text.component'

/* Styles ======================================================================================= */
import { mb8 } from '../../__aeki__/styles/styleset/margin/mb8'
import { ml16 } from '../../__aeki__/styles/styleset/margin/ml16'
import { mr4 } from '../../__aeki__/styles/styleset/margin/mr4'
import { mt12 } from '../../__aeki__/styles/styleset/margin/mt12'
import { mt8 } from '../../__aeki__/styles/styleset/margin/mt8'
import { p8 } from '../../__aeki__/styles/styleset/padding/p8'
import { paddingLeftInner } from '../../__aeki__/styles/default/padding'
import { pb12 } from '../../__aeki__/styles/styleset/padding/pb12'
import { pb8 } from '../../__aeki__/styles/styleset/padding/pb8'
import { pt12 } from '../../__aeki__/styles/styleset/padding/pt12'
import { mt4 } from '../../__aeki__/styles/styleset/margin/mt4'
import { Form } from '../../__aeki__/components/form'
import { Checkbox } from '../../__aeki__/components/checkbox/checkbox.component'
import useValue from '../../__aeki__/hooks/use-value'
import { Notification } from '../../__aeki__/components/notification'

export default addSnippet(
  'ChildcareLeadActions',
  withRouter((props: any) => {
    const { title, match, row } = props
    const { groupId, baseId, rowId } = match.params

    const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/childcare-lead-actions/${groupId}?row=${rowId}`
    const actionUrl = `${process.env.REACT_APP_API_URL}/a/snippets/childcare-lead-actions/${groupId}`
    const [data, $data] = useFetch(
      {
        classes: {
          count: 0,
          data: [],
        },
        programs: {
          count: 0,
          data: [],
        },
        programsForWaitlist: {
          count: 0,
          data: [],
        },
        programsForEnrollment: {
          count: 0,
          data: [],
        },
        waitlist: {
          count: 0,
          data: [],
        },
        enrollment: {
          count: 0,
          data: [],
        },
        registrationForms: {
          count: 0,
          data: [],
        },
      },
      { url: apiUrl, isLoading: true },
    )
    const [, $actions] = useFetch({})
    const [update, $update] = useFetch({})
    const [enrolledClasses, $enrolledClasses] = useValue({})
    const [enrolledClassesTouched, $enrolledClassesTouched] = useValue(false)

    useEffect(() => {
      if (data.mounted) {
        $data.get()
      }
    }, [data.mounted])

    useEffect(() => {
      if (row?.body?.enrolledClasses) {
        $enrolledClasses.set(row.body.enrolledClasses)
      }
    }, [row?.body?.enrolledClasses])

    useEffect(() => {
      if (enrolledClassesTouched && update.mounted) {
        Notification.push({
          label: 'Updating program / classes, please wait...',
          noTimeout: true,
          // baseline: 'error',
          // alt: true,
        })
        $update
          .patch({
            url: `${actionUrl}/update/${rowId}/enrolledClasses`,
            body: enrolledClasses,
          })
          .then(() => {
            Notification.push({
              label: 'Successfully updated program / classes',
              // baseline: 'error',
              // alt: true,
            })
          })
      }
    }, [enrolledClasses])

    const missingFields: string[] = []

    if (Object.keys(row).length > 0) {
      if (!row.body?.generalInformation?.primaryGuardian?.firstName) {
        missingFields.push('Primary Guardian / First Name')
      }
      if (!row.body?.generalInformation?.primaryGuardian?.lastName) {
        missingFields.push('Primary Guardian / Last Name')
      }
      if (!row.body?.generalInformation?.primaryGuardian?.phoneNumber) {
        missingFields.push('Primary Guardian / Phone Number')
      }
      if (!row.body?.generalInformation?.primaryGuardian?.relationship) {
        missingFields.push('Primary Guardian / Relationship')
      }

      if (!row.body?.generalInformation?.child?.firstName) {
        missingFields.push('Child / First Name')
      }
      if (!row.body?.generalInformation?.child?.lastName) {
        missingFields.push('Child / Last Name')
      }
      if (!row.body?.generalInformation?.child?.dateOfBirth) {
        missingFields.push('Child / Date of Birth')
      }
    }

    return (
      <Snippet>
        <SnippetHead title={title} />
        <Line />
        <Box className={pb8}>
          {baseId === 'childcare-leads' ? (
            <Box className={classes(paddingLeftInner, pb12, mt12)} row>
              <Button
                label="Move to the Client List"
                square
                baseline={missingFields.length > 0 ? 'disabled' : 'primary'}
                size="md"
                onClick={() => {
                  $actions
                    .post({
                      url: `${actionUrl}/move-to-clients/${rowId}`,
                    })
                    .then((payload: any) => {
                      window.location.href = `${process.env.REACT_APP_WEB_URL}${payload.to}`
                    })
                }}
              />
              <Text className={mt8} size="xs" light baseline="surface">
                To put this child and parent to the waitlist or enroll, you need to move them to the
                client list.
              </Text>

              {missingFields.length > 0 && (
                <>
                  <Text className={mt8} size="xs" light baseline="surface">
                    Please fill out following fields to do so:
                  </Text>
                  <Text tag={'ul'} className={mt8} size="xs" light baseline="surface">
                    {missingFields.map((field: any, key: any) => {
                      return (
                        <Text
                          tag={'li'}
                          className={ml16}
                          size="xs"
                          light
                          baseline="surface"
                          key={key}
                        >
                          {field}
                        </Text>
                      )
                    })}
                  </Text>
                </>
              )}
            </Box>
          ) : (
            <>
              {data.payload.programs.count === 0 && (
                <>
                  <Box className={classes(paddingLeftInner, pt12, pb12)} row>
                    <Text baseline="surface" size="xs" light className={mb8}>
                      Seems like you don&apos;t have a program set up. Please create a program first
                      by clicking the button below.
                    </Text>
                    <a href={`/a/groups/${groupId}/base/childcare-programs/table/new`}>
                      <Box row>
                        <Button label="Create a Program" square baseline={'primary'} size="md" />
                      </Box>
                    </a>
                  </Box>
                  <Box row>
                    <Line />
                  </Box>
                </>
              )}
              <Box className={classes(paddingLeftInner, pt12, pb12)} row>
                <Box flex spaceBetween alignCenterY>
                  <Text size="lg" semiBold light baseline="surface">
                    Registration Form
                  </Text>
                  <Button
                    label={'Send'}
                    square
                    baseline={'primary'}
                    size="xs"
                    onClick={async () => {
                      await import('../../modals/childcare-registration-form')
                      Modal.push({
                        type: 'ChildcareRegistrationForm',
                        componentProps: {
                          data: row,
                          label: 'Send Registration Form',
                          update: () => {
                            $data.get()
                          },
                        },
                      })
                    }}
                  />
                </Box>
                <Line className={classes(mt8, mb8)} />
                <Box row>
                  {!data.meta.isLoading && data.payload.registrationForms?.count === 0 && (
                    <Text size="xs" italic baseline="surface" lighter>
                      No forms are sent.
                    </Text>
                  )}
                  {data.payload.registrationForms?.data?.map?.((entry: any, key: any) => {
                    return (
                      <Box
                        key={key}
                        rounded
                        className={classes(
                          p8,
                          data.payload.registrationForms.data.length > 1 && key !== 0 && mt8,
                        )}
                        baseline="background"
                        border
                        row
                      >
                        <Text size="xs" baseline="surface" light>
                          Sent on {formatDate(entry.createdAt, { time: false })}
                        </Text>
                        <Text size="sm" baseline="surface" link={!!entry?.program?.id}>
                          {entry.body.title}
                        </Text>
                        <Text size="xs" baseline="surface" className={mt4}>
                          {entry.body.status}
                        </Text>
                      </Box>
                    )
                  })}
                </Box>
              </Box>

              <Box className={classes(paddingLeftInner, pt12, pb12)} row>
                <Box flex spaceBetween alignCenterY>
                  <Text size="lg" semiBold light baseline="surface">
                    Program / Classes
                  </Text>
                  {/* <Button
                    label={'Add'}
                    square
                    baseline={
                      data.payload.programsForEnrollment.count === 0 ? 'disabled' : 'primary'
                    }
                    size="xs"
                    onClick={async () => {
                      await import('../../modals/childcare-client-program')
                      Modal.push({
                        type: 'ChildcareClientProgram',
                        componentProps: {
                          data: data.payload.programsForEnrollment.data,
                          label: 'Add to Enrollment',
                          type: 'enrollment',
                          update: () => {
                            $data.get()
                          },
                        },
                      })
                    }}
                  /> */}
                </Box>
                <Line className={classes(mt8, mb8)} />
                <Box row>
                  {data.payload.classes.data
                    .sort((a: any, b: any) => {
                      const nameA = a?.body?.generalInformation?.name?.toUpperCase()
                      const nameB = b?.body?.generalInformation?.name?.toUpperCase()
                      if (nameA < nameB) {
                        return -1
                      }
                      if (nameA > nameB) {
                        return 1
                      }
                      return 0
                    })
                    .map((entry: any, key: any) => {
                      return (
                        <Box
                          key={key}
                          rounded
                          className={classes(
                            data.payload.classes.data.length > 1 && key !== 0 && mt8,
                          )}
                          row
                        >
                          <Checkbox
                            value={enrolledClasses[entry.id]}
                            onClick={async value => {
                              $enrolledClassesTouched.set(true)
                              $enrolledClasses.set(prev => {
                                return { ...prev, [entry.id]: value }
                              })
                            }}
                            label={
                              <Box row>
                                <Text size="xs" baseline="surface" lighter>
                                  {entry.body.program.body.generalInformation.name}
                                </Text>
                                <Text>{entry?.body?.generalInformation?.name}</Text>
                              </Box>
                            }
                          />
                        </Box>
                      )
                    })}
                </Box>
              </Box>
              {/* <Box row>
                <Line />
              </Box>
              <Box className={classes(paddingLeftInner, pt12, pb12)} row>
                <Box flex spaceBetween alignCenterY>
                  <Text size="lg" semiBold light baseline="surface">
                    Waitlist
                  </Text>
                  <Button
                    label="Add"
                    square
                    baseline={
                      data.payload.programsForWaitlist.data.length === 0 ? 'disabled' : 'primary'
                    }
                    size="xs"
                    onClick={async () => {
                      await import('../../modals/childcare-client-program')
                      Modal.push({
                        type: 'ChildcareClientProgram',
                        componentProps: {
                          data: data.payload.programsForWaitlist.data,
                          label: 'Add to Waitlist',
                          type: 'waitlist',
                          update: () => {
                            $data.get()
                          },
                        },
                      })
                    }}
                  />
                </Box>
                <Line className={classes(mt8, mb8)} />
                <Box row>
                  {!data.meta.isLoading && data.payload.waitlist.count === 0 && (
                    <Text size="xs" italic baseline="surface" lighter>
                      Waitlist is empty
                    </Text>
                  )}
                  {data.payload.waitlist.data.map((entry: any, key: any) => {
                    return (
                      <Box
                        key={key}
                        rounded
                        className={classes(
                          p8,
                          data.payload.waitlist.data.length > 1 && key !== 0 && mt8,
                        )}
                        baseline="background"
                        border
                        row
                      >
                        <Text size="xs" baseline="surface" light>
                          Listed on {formatDate(entry.createdAt, { time: false })}
                        </Text>
                        <a
                          href={
                            entry?.program?.id
                              ? `/a/groups/${groupId}/base/childcare-waitlist/parent/${entry?.program?.id}/table`
                              : ''
                          }
                        >
                          <Text size="xl" semiBold baseline="surface" link={!!entry?.program?.id}>
                            {entry?.program?.body?.generalInformation?.name ||
                              (entry?.program?.id ? '' : 'Deleted')}
                          </Text>
                        </a>
                        <Line className={classes(mt8, mb8)} dark />
                        <Box flex baseline="none" alignRight>
                          <Button
                            label="Remove"
                            square
                            size="xs"
                            className={mr4}
                            baseline="background"
                            onClick={async () => {
                              $actions
                                .post({
                                  url: `${actionUrl}/remove-waitlist/${rowId}?&delete=${entry?.id}`,
                                })
                                .then(() => {
                                  $data.get()
                                })
                            }}
                          />
                          <Button
                            label="Enroll"
                            square
                            size="xs"
                            baseline="primary"
                            onClick={async () => {
                              $actions
                                .post({
                                  url: `${actionUrl}/add-to-enrollment/${rowId}?program=${entry.program.id}&delete=${entry?.id}`,
                                })
                                .then(() => {
                                  $data.get()
                                })
                            }}
                          />
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              </Box> */}
            </>
          )}
        </Box>
      </Snippet>
    )
  }),
)
